/**
 * NdfTiersPanel — écran 6 du mockup : annuaire des tiers + création d'une note
 * de frais pour un tiers par la comptabilité. Reproduit le mockup à l'identique.
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { Plus, Paperclip } from 'lucide-react';
import { compressImageIfNeeded } from '@/lib/image-compress';
import type { NdfTiers, NdfTypeFrais } from '@roronoa-collectivites/shared-types';

const COMPTA = 'oklch(var(--c-compta))';
const eur = (n: number) => `${n.toFixed(2).replace('.', ',')} €`;
const TYPE_LABEL: Record<string, string> = { prestataire: 'Prestataire', intervenant: 'Intervenant', beneficiaire: 'Bénéficiaire', autre: 'Autre' };

export function NdfTiersPanel() {
  const [tiers, setTiers] = useState<NdfTiers[]>([]);
  const [types, setTypes] = useState<NdfTypeFrais[]>([]);
  const [q, setQ] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [edit, setEdit] = useState<NdfTiers | null>(null);
  const [creating, setCreating] = useState(false);
  const [noteFor, setNoteFor] = useState<NdfTiers | null>(null);

  async function charger() {
    try {
      const [ti, ty] = await Promise.all([api.notesFrais.tiers.list(q || undefined), api.notesFrais.types.list(true)]);
      setTiers(ti); setTypes(ty);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  useEffect(() => { void charger(); }, []);
  useEffect(() => { const t = setTimeout(() => void charger(), 300); return () => clearTimeout(t); }, [q]);

  return (
    <div className="space-y-4">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      <Card variant="glass" padded={false}>
        <div className="flex flex-wrap items-center gap-2.5 border-b border-border px-4 py-3">
          <h3 className="text-sm font-semibold text-ink">Tiers enregistrés</h3>
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher…" className="ml-auto h-8 w-56 rounded-r-md border border-border bg-surface/60 px-2.5 text-xs text-ink" />
          <Button variant="secondary" size="sm" style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => setCreating(true)}><Plus className="h-4 w-4" /> Ajouter un tiers</Button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-surface/40 text-left text-[10px] uppercase tracking-wider text-ink-subtle">
                <th className="px-4 py-2.5 font-medium">Nom</th><th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium">SIRET</th><th className="px-4 py-2.5 font-medium">Ville</th>
                <th className="px-4 py-2.5 font-medium">Contact</th><th className="px-4 py-2.5 font-medium">Actif</th><th className="px-4 py-2.5 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.id} className="border-b border-border/60 last:border-0">
                  <td className="px-4 py-2.5 text-ink">{t.nom}</td>
                  <td className="px-4 py-2.5"><Badge variant={t.type === 'intervenant' ? 'info' : 'neutral'}>{TYPE_LABEL[t.type] ?? t.type}</Badge></td>
                  <td className="px-4 py-2.5 text-ink-subtle">{t.siret ?? '—'}</td>
                  <td className="px-4 py-2.5 text-ink-muted">{t.ville ?? '—'}</td>
                  <td className="px-4 py-2.5 text-ink-subtle">{t.email ?? t.telephone ?? '—'}</td>
                  <td className="px-4 py-2.5">{t.actif ? <Badge variant="success">Actif</Badge> : <Badge variant="neutral">Inactif</Badge>}</td>
                  <td className="px-4 py-2.5 text-right"><Button variant="ghost" size="sm" onClick={() => setEdit(t)}>Modifier</Button>{' '}<Button variant="secondary" size="sm" style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => setNoteFor(t)}><Plus className="h-3.5 w-3.5" /> Note de frais</Button></td>
                </tr>
              ))}
              {tiers.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-sm text-ink-subtle">Aucun tiers.</td></tr>}
            </tbody>
          </table>
        </div>
        <div className="border-t border-border px-4 py-2.5 text-xs text-ink-muted">L'IBAN d'un tiers (pour virement) est une donnée sensible : visible par la comptabilité uniquement, jamais exposée aux déclarants.</div>
      </Card>

      {(edit || creating) && <TiersModal tiers={edit} onClose={() => { setEdit(null); setCreating(false); }} onSaved={() => { setEdit(null); setCreating(false); void charger(); }} />}
      {noteFor && <NotePourTiersModal tiers={noteFor} types={types} onClose={() => setNoteFor(null)} onSaved={() => { setNoteFor(null); setMsg('Note créée — en attente de validation comptable.'); }} />}
    </div>
  );
}

function TiersModal({ tiers, onClose, onSaved }: { tiers: NdfTiers | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({
    type: tiers?.type ?? 'prestataire', nom: tiers?.nom ?? '', siret: tiers?.siret ?? '', adresse: tiers?.adresse ?? '',
    codePostal: tiers?.codePostal ?? '', ville: tiers?.ville ?? '', email: tiers?.email ?? '', telephone: tiers?.telephone ?? '',
    iban: tiers?.iban ?? '', actif: tiers?.actif ?? true, noteInterne: tiers?.noteInterne ?? '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inp = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink';
  async function save() {
    setSaving(true); setError(null);
    const body = { ...f, type: f.type as NdfTiers['type'], siret: f.siret || null, adresse: f.adresse || null, codePostal: f.codePostal || null, ville: f.ville || null, email: f.email || null, telephone: f.telephone || null, iban: f.iban || null, noteInterne: f.noteInterne || null };
    try { if (tiers) await api.notesFrais.tiers.update(tiers.id, body); else await api.notesFrais.tiers.create(body); onSaved(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); } finally { setSaving(false); }
  }
  return (
    <Dialog open onClose={onClose} title={tiers ? 'Modifier le tiers' : 'Ajouter un tiers'}
      footer={<><Button variant="ghost" onClick={onClose}>Annuler</Button><Button variant="secondary" loading={saving} disabled={!f.nom.trim()} style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => void save()}>Enregistrer</Button></>}>
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <div className="grid grid-cols-2 gap-3">
          <div><div className="mb-1 text-xs text-ink-subtle">Nom</div><input value={f.nom} onChange={(e) => setF((s) => ({ ...s, nom: e.target.value }))} className={inp} /></div>
          <div><div className="mb-1 text-xs text-ink-subtle">Type</div>
            <select value={f.type} onChange={(e) => setF((s) => ({ ...s, type: e.target.value as NdfTiers['type'] }))} className={inp}>
              <option value="prestataire">Prestataire</option><option value="intervenant">Intervenant</option><option value="beneficiaire">Bénéficiaire</option><option value="autre">Autre</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="mb-1 text-xs text-ink-subtle">SIRET</div><input value={f.siret} onChange={(e) => setF((s) => ({ ...s, siret: e.target.value }))} className={inp} /></div>
          <div><div className="mb-1 text-xs text-ink-subtle">Ville</div><input value={f.ville} onChange={(e) => setF((s) => ({ ...s, ville: e.target.value }))} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="mb-1 text-xs text-ink-subtle">Adresse</div><input value={f.adresse} onChange={(e) => setF((s) => ({ ...s, adresse: e.target.value }))} className={inp} /></div>
          <div><div className="mb-1 text-xs text-ink-subtle">Code postal</div><input value={f.codePostal} onChange={(e) => setF((s) => ({ ...s, codePostal: e.target.value }))} className={inp} /></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="mb-1 text-xs text-ink-subtle">Email</div><input value={f.email} onChange={(e) => setF((s) => ({ ...s, email: e.target.value }))} className={inp} /></div>
          <div><div className="mb-1 text-xs text-ink-subtle">Téléphone</div><input value={f.telephone} onChange={(e) => setF((s) => ({ ...s, telephone: e.target.value }))} className={inp} /></div>
        </div>
        <div><div className="mb-1 text-xs text-ink-subtle">IBAN (sensible — compta uniquement)</div><input value={f.iban} onChange={(e) => setF((s) => ({ ...s, iban: e.target.value }))} className={inp} /></div>
        <label className="flex items-center gap-2 text-sm text-ink"><input type="checkbox" checked={f.actif} onChange={(e) => setF((s) => ({ ...s, actif: e.target.checked }))} /> Actif</label>
      </div>
    </Dialog>
  );
}

function NotePourTiersModal({ tiers, types, onClose, onSaved }: { tiers: NdfTiers; types: NdfTypeFrais[]; onClose: () => void; onSaved: () => void }) {
  const [dateNote, setDateNote] = useState(new Date().toISOString().slice(0, 10));
  const [typeFraisId, setTypeFraisId] = useState(types.find((t) => !t.baremeKm)?.id ?? types[0]?.id ?? '');
  const [libelle, setLibelle] = useState('');
  const [montant, setMontant] = useState('');
  const [tva, setTva] = useState('');
  const [piece, setPiece] = useState<{ r2Key: string; nom: string } | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inp = 'h-10 w-full rounded-r-md border border-border bg-surface/60 px-2.5 text-sm text-ink';

  async function choisir(file: File) {
    try { const c = await compressImageIfNeeded(file); const up = await api.notesFrais.uploadJustificatif(c); setPiece(up); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Upload impossible'); }
  }
  async function save() {
    setSaving(true); setError(null);
    try {
      await api.notesFrais.notes.create({
        titre: `Note de frais — ${tiers.nom}`, dateNote, tiersId: tiers.id, envoyer: true,
        lignes: [{ typeFraisId, dateDepense: dateNote, libelle, montantTtc: Number(montant.replace(',', '.')) || 0, montantTva: tva ? Number(tva.replace(',', '.')) : null, justificatifR2Key: piece?.r2Key ?? null, justificatifNom: piece?.nom ?? null }],
      });
      onSaved();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); } finally { setSaving(false); }
  }
  return (
    <Dialog open onClose={onClose} title={`Note de frais pour un tiers — ${tiers.nom}`}
      footer={<><Button variant="ghost" onClick={onClose}>Annuler</Button><Button variant="secondary" loading={saving} disabled={!montant} style={{ background: COMPTA, borderColor: 'transparent', color: '#fff' }} onClick={() => void save()}>Créer la note (→ à valider compta)</Button></>}>
      <div className="space-y-3">
        {error && <Alert variant="error">{error}</Alert>}
        <div className="rounded-r-lg border border-border bg-rh-tint/40 px-3 py-2 text-xs text-ink-muted">Créée par la comptabilité pour un intervenant extérieur. Entre directement en file « à valider » côté compta (pas de niveau manager).</div>
        <div className="grid grid-cols-2 gap-3">
          <div><div className="mb-1 text-xs text-ink-subtle">Tiers bénéficiaire</div><input value={tiers.nom} readOnly className={inp} /></div>
          <div><div className="mb-1 text-xs text-ink-subtle">Date</div><input type="date" value={dateNote} onChange={(e) => setDateNote(e.target.value)} className={inp} /></div>
        </div>
        <div className="rounded-r-lg border border-border bg-surface/40 p-3">
          <div className="grid grid-cols-3 gap-2.5">
            <div><div className="mb-1 text-xs text-ink-subtle">Type de frais</div>
              <select value={typeFraisId} onChange={(e) => setTypeFraisId(e.target.value)} className={inp}>{types.filter((t) => !t.baremeKm).map((t) => <option key={t.id} value={t.id}>{t.libelle}</option>)}</select>
            </div>
            <div><div className="mb-1 text-xs text-ink-subtle">Montant TTC</div><input value={montant} onChange={(e) => setMontant(e.target.value)} className={inp} /></div>
            <div><div className="mb-1 text-xs text-ink-subtle">Dont TVA</div><input value={tva} onChange={(e) => setTva(e.target.value)} className={inp} /></div>
          </div>
          <div className="mt-2"><div className="mb-1 text-xs text-ink-subtle">Libellé</div><input value={libelle} onChange={(e) => setLibelle(e.target.value)} className={inp} /></div>
          <div className="mt-2">
            {piece
              ? <div className="rounded-r-lg border border-success/50 bg-success/10 px-3 py-2 text-xs text-success">✓ {piece.nom}</div>
              : <label className="block cursor-pointer rounded-r-lg border border-dashed border-border-hi px-3 py-2.5 text-center text-xs text-ink-subtle hover:border-border"><Paperclip className="mr-1 inline h-3.5 w-3.5" />Joindre la facture (PDF, JPG, PNG)<input type="file" accept=".pdf,image/*" hidden onChange={(e) => e.target.files?.[0] && choisir(e.target.files[0])} /></label>}
          </div>
        </div>
        <p className="text-xs text-ink-subtle">Montant : {montant ? eur(Number(montant.replace(',', '.')) || 0) : '—'}</p>
      </div>
    </Dialog>
  );
}
